import React, { useState, useMemo } from 'react';
import ProfileTestPreviewModal from './ProfileTestPreviewModal';
import DuplicateTestAlertModal from './DuplicateTestAlertModal';

export const PackageComponentsStep = ({
  allTests = [],
  allProfiles = [],
  loadingCatalog = false,
  selectedComponents = [],
  onAddComponent,
  onRemoveComponent,
  onMoveUp,
  onMoveDown,
  onBack,
  onNext
}) => {
  // Active catalog tab: 'TEST' | 'PROFILE'
  const [activeTab, setActiveTab] = useState('PROFILE');
  const [catalogSearch, setCatalogSearch] = useState('');

  // Modals state
  const [previewProfile, setPreviewProfile] = useState(null);
  const [duplicateAlert, setDuplicateAlert] = useState(null); // { test, parentProfileName }

  // Set of selected component keys
  const selectedKeySet = useMemo(() => {
    return new Set(selectedComponents.map(c => `${c.componentType}_${c.componentId}`));
  }, [selectedComponents]);

  // Map of test IDs that are included inside selected profiles
  // (Profiles in allProfiles may contain .components array if preloaded, or activeComponentCount)
  const profileContainedTestMap = useMemo(() => {
    const map = new Map(); // testId -> profileName
    const selectedProfileIds = new Set(
      selectedComponents.filter(c => c.componentType === 'PROFILE').map(c => c.componentId)
    );

    allProfiles.forEach(p => {
      if (selectedProfileIds.has(p.profileId) && Array.isArray(p.components)) {
        p.components.forEach(comp => {
          const tId = comp.investigationId || comp.id;
          if (tId && !map.has(tId)) {
            map.set(tId, p.profileName || p.name);
          }
        });
      }
    });

    return map;
  }, [selectedComponents, allProfiles]);

  // Filtered available Tests
  const filteredTests = useMemo(() => {
    const q = catalogSearch.trim().toLowerCase();
    return allTests.filter(t => {
      if (!q) return true;
      const name = (t.name || t.investigationName || '').toLowerCase();
      const code = (t.testCode || t.code || '').toLowerCase();
      const dept = (t.subCategoryName || t.departmentName || '').toLowerCase();
      return name.includes(q) || code.includes(q) || dept.includes(q);
    });
  }, [allTests, catalogSearch]);

  // Filtered available Profiles
  const filteredProfiles = useMemo(() => {
    const q = catalogSearch.trim().toLowerCase();
    return allProfiles.filter(p => {
      if (!q) return true;
      const name = (p.profileName || p.name || '').toLowerCase();
      const code = (p.profileCode || p.code || '').toLowerCase();
      const desc = (p.description || '').toLowerCase();
      return name.includes(q) || code.includes(q) || desc.includes(q);
    });
  }, [allProfiles, catalogSearch]);

  // Handler for adding a test with duplicate detection
  const handleAttemptAddTest = (test) => {
    const testId = test.investigationId || test.id;
    const isAlreadySelected = selectedKeySet.has(`TEST_${testId}`);
    if (isAlreadySelected) return;

    // Check if test is already part of an included profile
    if (profileContainedTestMap.has(testId)) {
      setDuplicateAlert({
        test,
        parentProfileName: profileContainedTestMap.get(testId)
      });
      return;
    }

    // Otherwise add directly
    onAddComponent({
      componentType: 'TEST',
      componentId: testId,
      code: test.testCode || test.code,
      name: test.name || test.investigationName,
      departmentName: test.subCategoryName || test.departmentName || 'General Pathology'
    });
  };

  const handleConfirmDuplicateAdd = () => {
    if (!duplicateAlert?.test) return;
    const test = duplicateAlert.test;
    const testId = test.investigationId || test.id;
    onAddComponent({
      componentType: 'TEST',
      componentId: testId,
      code: test.testCode || test.code,
      name: test.name || test.investigationName,
      departmentName: test.subCategoryName || test.departmentName || 'General Pathology'
    });
    setDuplicateAlert(null);
  };

  // Handler for adding a profile
  const handleAddProfile = (prof) => {
    const profId = prof.profileId || prof.id;
    if (selectedKeySet.has(`PROFILE_${profId}`)) return;

    const count = prof.activeComponentCount ?? prof.testCount ?? (Array.isArray(prof.components) ? prof.components.length : 0);

    onAddComponent({
      componentType: 'PROFILE',
      componentId: profId,
      code: prof.profileCode || prof.code,
      name: prof.profileName || prof.name,
      departmentName: prof.departmentName || 'Clinical Profile Group',
      testCount: count,
      components: prof.components || []
    });
  };

  // Summary tallies
  const selectedProfiles = useMemo(() => {
    return selectedComponents.filter(c => c.componentType === 'PROFILE');
  }, [selectedComponents]);

  const selectedDirectTests = useMemo(() => {
    return selectedComponents.filter(c => c.componentType === 'TEST');
  }, [selectedComponents]);

  const profileTestsCount = useMemo(() => {
    return selectedProfiles.reduce((sum, p) => {
      const match = allProfiles.find(ap => ap.profileId === p.componentId);
      const count = match?.activeComponentCount ?? p.testCount ?? (Array.isArray(p.components) ? p.components.length : 0);
      return sum + (Number(count) || 0);
    }, 0);
  }, [selectedProfiles, allProfiles]);

  const totalEstimatedTests = profileTestsCount + selectedDirectTests.length;

  return (
    <div className="pkg-builder-container fade-in">
      {/* Two-Panel Layout */}
      <div className="pkg-builder-grid">
        {/* ================= LEFT PANEL: CATALOG ================= */}
        <div className="pkg-builder-panel pkg-catalog-panel">
          <div className="pkg-panel-header">
            <div className="pkg-panel-header-title">
              <span className="pkg-panel-icon">➕</span>
              <h4>Add Components</h4>
            </div>
            <span className="pkg-panel-tag">Source Catalogs</span>
          </div>

          {/* Catalog Tabs: Tests vs Profiles */}
          <div className="pkg-catalog-tabs">
            <button
              type="button"
              className={`pkg-catalog-tab-btn ${activeTab === 'PROFILE' ? 'active' : ''}`}
              onClick={() => setActiveTab('PROFILE')}
              id="pkg-tab-profiles"
            >
              <span className="pkg-tab-icon">📋</span>
              <span>Profiles</span>
              <span className="pkg-tab-count">({allProfiles.length})</span>
            </button>
            <button
              type="button"
              className={`pkg-catalog-tab-btn ${activeTab === 'TEST' ? 'active' : ''}`}
              onClick={() => setActiveTab('TEST')}
              id="pkg-tab-tests"
            >
              <span className="pkg-tab-icon">🧪</span>
              <span>Tests</span>
              <span className="pkg-tab-count">({allTests.length})</span>
            </button>
          </div>

          {/* Unified Search Box */}
          <div className="pkg-search-bar">
            <span className="pkg-search-lens">🔍</span>
            <input
              type="text"
              className="pkg-search-field"
              placeholder={activeTab === 'PROFILE' ? 'Search profiles by name, code...' : 'Search clinical tests by name, code, department...'}
              value={catalogSearch}
              onChange={(e) => setCatalogSearch(e.target.value)}
            />
            {catalogSearch && (
              <button
                type="button"
                className="pkg-search-clear-btn"
                onClick={() => setCatalogSearch('')}
                title="Clear search"
              >
                ✕
              </button>
            )}
          </div>

          {/* Catalog Items List */}
          <div className="pkg-catalog-list">
            {loadingCatalog ? (
              <div className="pkg-loading-state">
                <div className="pkg-spinner" />
                <span>Loading clinical catalog items...</span>
              </div>
            ) : activeTab === 'PROFILE' ? (
              filteredProfiles.length === 0 ? (
                <div className="pkg-panel-empty">
                  <span className="pkg-empty-icon">📋</span>
                  <span className="pkg-empty-title">
                    {catalogSearch ? 'No matching profiles found.' : 'No profiles available.'}
                  </span>
                  <span className="pkg-empty-sub">
                    {catalogSearch ? 'Try adjusting your search terms.' : 'Create clinical profiles in Profile Master first.'}
                  </span>
                </div>
              ) : (
                filteredProfiles.map((prof) => {
                  const profId = prof.profileId || prof.id;
                  const isAdded = selectedKeySet.has(`PROFILE_${profId}`);
                  const count = prof.activeComponentCount ?? (Array.isArray(prof.components) ? prof.components.length : 0);

                  return (
                    <div
                      key={profId}
                      className={`pkg-catalog-card profile-card ${isAdded ? 'is-added' : ''}`}
                    >
                      <div className="pkg-card-top-row">
                        <span className="pkg-badge-profile">PROFILE</span>
                        {count > 0 && (
                          <span className="pkg-test-count-badge">
                            <strong>{count}</strong> {count === 1 ? 'Test' : 'Tests'} Included
                          </span>
                        )}
                      </div>

                      <div className="pkg-card-main">
                        <h5 className="pkg-card-name">{prof.profileName || prof.name}</h5>
                        <div className="pkg-card-code-line">
                          <span>Code: <strong>{prof.profileCode || prof.code || '—'}</strong></span>
                          {prof.departmentName && <span>• {prof.departmentName}</span>}
                        </div>
                        {prof.description && (
                          <p className="pkg-card-desc-snippet">{prof.description}</p>
                        )}
                      </div>

                      <div className="pkg-card-actions">
                        <button
                          type="button"
                          className="pkg-btn-secondary-sm"
                          onClick={() => setPreviewProfile(prof)}
                          title="Inspect tests included inside this profile"
                        >
                          👁️ View Tests
                        </button>

                        <button
                          type="button"
                          className={`pkg-btn-add-sm ${isAdded ? 'added' : ''}`}
                          onClick={() => handleAddProfile(prof)}
                          disabled={isAdded}
                        >
                          {isAdded ? '✓ Added' : '+ Add Profile'}
                        </button>
                      </div>
                    </div>
                  );
                })
              )
            ) : (
              filteredTests.length === 0 ? (
                <div className="pkg-panel-empty">
                  <span className="pkg-empty-icon">🧪</span>
                  <span className="pkg-empty-title">
                    {catalogSearch ? 'No matching tests found.' : 'No tests available.'}
                  </span>
                  <span className="pkg-empty-sub">
                    {catalogSearch ? 'Try another search keyword.' : 'Configure investigations in Investigation Master.'}
                  </span>
                </div>
              ) : (
                filteredTests.map((test) => {
                  const testId = test.investigationId || test.id;
                  const isAdded = selectedKeySet.has(`TEST_${testId}`);
                  const isInsideProfile = profileContainedTestMap.has(testId);

                  return (
                    <div
                      key={testId}
                      className={`pkg-catalog-card test-card ${isAdded ? 'is-added' : ''}`}
                    >
                      <div className="pkg-card-top-row">
                        <span className="pkg-badge-test">TEST</span>
                        {isInsideProfile && (
                          <span className="pkg-included-hint" title={`Already inside ${profileContainedTestMap.get(testId)}`}>
                            Inside: {profileContainedTestMap.get(testId)}
                          </span>
                        )}
                      </div>

                      <div className="pkg-card-main">
                        <h5 className="pkg-card-name">{test.name || test.investigationName}</h5>
                        <div className="pkg-card-code-line">
                          <span>Code: <strong>{test.testCode || test.code || '—'}</strong></span>
                          <span>• {test.subCategoryName || test.departmentName || 'General'}</span>
                        </div>
                      </div>

                      <div className="pkg-card-actions">
                        <button
                          type="button"
                          className={`pkg-btn-add-sm ${isAdded ? 'added' : ''}`}
                          onClick={() => handleAttemptAddTest(test)}
                          disabled={isAdded}
                        >
                          {isAdded ? '✓ Added' : '+ Add Test'}
                        </button>
                      </div>
                    </div>
                  );
                })
              )
            )}
          </div>
        </div>

        {/* ================= RIGHT PANEL: SELECTED COMPONENTS ================= */}
        <div className="pkg-builder-panel pkg-selected-panel">
          <div className="pkg-panel-header">
            <div className="pkg-panel-header-title">
              <span className="pkg-panel-icon">📦</span>
              <h4>Package Components</h4>
            </div>
            <span className="pkg-selection-count">
              <strong>{selectedComponents.length}</strong> {selectedComponents.length === 1 ? 'Selected' : 'Selected'}
            </span>
          </div>

          <div className="pkg-selected-list">
            {selectedComponents.length === 0 ? (
              <div className="pkg-selected-empty">
                <div className="pkg-empty-illustration">📥</div>
                <h5>No Components Added Yet</h5>
                <p>
                  Build your health package by clicking <strong>+ Add Profile</strong> or <strong>+ Add Test</strong> from the left panel.
                </p>
                <div className="pkg-empty-recommendation">
                  <span>💡 Tip:</span> You can mix multi-test Profiles (e.g. CBC, Lipid Profile) and standalone tests (e.g. Vitamin D, HbA1c) in the same package.
                </div>
              </div>
            ) : (
              selectedComponents.map((item, index) => {
                const isProfile = item.componentType === 'PROFILE';

                return (
                  <div
                    key={`${item.componentType}_${item.componentId}`}
                    className={`pkg-selected-card ${isProfile ? 'selected-profile' : 'selected-test'}`}
                  >
                    <div className="pkg-selected-order-num">
                      #{index + 1}
                    </div>

                    <div className="pkg-selected-details">
                      <div className="pkg-card-top-row">
                        <span className={isProfile ? 'pkg-badge-profile' : 'pkg-badge-test'}>
                          {isProfile ? 'PROFILE' : 'TEST'}
                        </span>
                        {isProfile && (
                          <span className="pkg-test-count-badge">
                            <strong>{item.testCount || 'Group'}</strong> {item.testCount === 1 ? 'Test' : 'Tests'} Included
                          </span>
                        )}
                        {!isProfile && (
                          <span className="pkg-direct-test-tag">Direct Test</span>
                        )}
                      </div>

                      <h5 className="pkg-selected-name">{item.name}</h5>
                      <span className="pkg-selected-meta">
                        Code: <strong>{item.code || '—'}</strong> • {item.departmentName || 'Diagnostics'}
                      </span>
                    </div>

                    <div className="pkg-selected-controls">
                      {isProfile && (
                        <button
                          type="button"
                          className="pkg-btn-secondary-xs"
                          onClick={() => {
                            const profMatch = allProfiles.find(ap => ap.profileId === item.componentId);
                            setPreviewProfile(profMatch || item);
                          }}
                          title="View included tests"
                        >
                          👁️ View Tests
                        </button>
                      )}

                      {/* Reorder Buttons */}
                      <div className="pkg-reorder-group">
                        <button
                          type="button"
                          className="pkg-reorder-btn"
                          onClick={() => onMoveUp(index)}
                          disabled={index === 0}
                          title="Move up"
                        >
                          ▲
                        </button>
                        <button
                          type="button"
                          className="pkg-reorder-btn"
                          onClick={() => onMoveDown(index)}
                          disabled={index === selectedComponents.length - 1}
                          title="Move down"
                        >
                          ▼
                        </button>
                      </div>

                      {/* Remove Button */}
                      <button
                        type="button"
                        className="pkg-btn-remove"
                        onClick={() => onRemoveComponent(item)}
                        title="Remove from package"
                      >
                        ✕ Remove
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* PACKAGE SUMMARY FOOTER */}
          <div className="pkg-summary-box">
            <div className="pkg-summary-header">
              <span className="pkg-summary-title">PACKAGE COMPOSITION SUMMARY</span>
              <span className="pkg-summary-total-tag">
                Estimated Total Tests: <strong>{totalEstimatedTests}</strong>
              </span>
            </div>

            <div className="pkg-summary-grid">
              <div className="pkg-summary-metric">
                <span className="pkg-metric-val">{selectedProfiles.length}</span>
                <span className="pkg-metric-label">Profiles</span>
              </div>
              <div className="pkg-summary-divider">+</div>
              <div className="pkg-summary-metric">
                <span className="pkg-metric-val">{selectedDirectTests.length}</span>
                <span className="pkg-metric-label">Direct Tests</span>
              </div>
              <div className="pkg-summary-divider">=</div>
              <div className="pkg-summary-metric highlight">
                <span className="pkg-metric-val">{totalEstimatedTests}</span>
                <span className="pkg-metric-label">Total Tests</span>
              </div>
            </div>

            <div className="pkg-summary-sentence">
              {selectedProfiles.length === 0 && selectedDirectTests.length === 0 ? (
                'No components added. Please add at least 1 test or profile to proceed.'
              ) : (
                <span>
                  <strong>{selectedProfiles.length}</strong> {selectedProfiles.length === 1 ? 'Profile contains' : 'Profiles contain'}{' '}
                  <strong>{profileTestsCount}</strong> tests • <strong>{selectedDirectTests.length}</strong> direct {selectedDirectTests.length === 1 ? 'test' : 'tests'} added.
                </span>
              )}
            </div>
          </div>

          {/* Builder Navigation Actions */}
          <div className="pkg-builder-nav-footer">
            <button type="button" className="btn btn-secondary" onClick={onBack}>
              ← Edit Details
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={onNext}
              disabled={selectedComponents.length === 0}
              id="pkg-builder-review-btn"
            >
              Review Package & Save →
            </button>
          </div>
        </div>
      </div>

      {/* Modals */}
      {previewProfile && (
        <ProfileTestPreviewModal
          profile={previewProfile}
          onClose={() => setPreviewProfile(null)}
        />
      )}

      {duplicateAlert && (
        <DuplicateTestAlertModal
          test={duplicateAlert.test}
          parentProfileName={duplicateAlert.parentProfileName}
          onCancel={() => setDuplicateAlert(null)}
          onConfirmAdd={handleConfirmDuplicateAdd}
        />
      )}
    </div>
  );
};

export default PackageComponentsStep;
